import React from 'react';
import { Button, Modal, Upload, message } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
    GET_ORDER_QUOTA_CUSTOMER_TIERS,
    GET_ORDER_QUOTA_HUB_OVERVIEW,
    IMPORT_ORDER_QUOTA_CUSTOMER_TIERS,
    OrderQuotaCustomerTier,
    OrderQuotaHubSummary,
    OrderQuotaImportResult,
    OrderQuotaStrategy,
} from '../graphql/orderQuotaGraphql';
import { useSheetImport } from '../hooks/useSheetImport';
import {
    FixedHeaders,
    buildTierWorkbook,
    downloadWorkbook,
    fixedHeaderOptions,
    parseTierSheet,
    templateTextOptions,
    tierFileName,
} from '../utils/orderQuotaExcel';
import ImportPreview, { ImportRejection } from './ImportPreview';
import LineList from './LineList';

/** 客户分级文件前三列表头：下载与上传校验共用 */
const TIER_HEADER_KEYS: FixedHeaders = [
    'central.orderQuota.import.codeHeader.1',
    'central.orderQuota.import.nameHeader',
    'central.orderQuota.customerTier.tierHeader',
];

/** 测试 HUB 列表头上的「可不填」标注：下载时加上，上传时去掉 */
const OPTIONAL_HUB_MARK_KEY = 'central.orderQuota.customerTier.optionalHubMark';

interface CustomerTierModalProps {
    open: boolean;
    /** 美西今日，用于文件名 */
    today: string;
    onCancel: () => void;
    /** 分级已写入后通知页面刷新 */
    onApplied: () => Promise<void>;
}

/**
 * 客户分级弹窗：下载当前分级与各尾程 HUB 的值（Excel）→ 修改后上传 → 预览 → 确认导入。
 * 只合并：文件中没有的客户不变，「普通」表示取消分级；分级变化为高优 / 低优的客户每个正式 HUB 都必须填值，
 * 测试 HUB 列（排在最后，表头带标注）选填；导入后任一 HUB 不满足产能配置要求时后端整体拒绝。
 */
const CustomerTierModal: React.FC<CustomerTierModalProps> = ({ open, today, onCancel, onApplied }) => {
    const { t, i18n } = useTranslation();
    const [loadTiers] = useLazyQuery<{ onboardingOrderQuotaCustomerTiers: OrderQuotaCustomerTier[] }>(
        GET_ORDER_QUOTA_CUSTOMER_TIERS,
        { fetchPolicy: 'network-only' }
    );
    /* HUB 列来自分拣产能管理的 HUB 列表（与分级导入接受的 HUB 一致），没有分级客户时模板也带 HUB 列 */
    const [loadHubs] = useLazyQuery<{ onboardingOrderQuotaHubOverview: OrderQuotaHubSummary[] }>(GET_ORDER_QUOTA_HUB_OVERVIEW, {
        variables: { strategyType: OrderQuotaStrategy.CUSTOMER_PICKUP_HUB },
    });
    const [importTiers] = useMutation<{ importOnboardingOrderQuotaCustomerTiers: OrderQuotaImportResult }>(
        IMPORT_ORDER_QUOTA_CUSTOMER_TIERS
    );
    const translate = (language: string, key: string) => i18n.getFixedT(language)(key);
    const { preview, rejection, loading, blocked, handleFile, handleConfirm } = useSheetImport({
        open,
        parse: (data) =>
            parseTierSheet(data, fixedHeaderOptions(TIER_HEADER_KEYS, translate), templateTextOptions(OPTIONAL_HUB_MARK_KEY, translate)),
        onImport: async (importRows, dryRun) => {
            const result = await importTiers({ variables: { rows: importRows, dryRun } });
            return result.data!.importOnboardingOrderQuotaCustomerTiers;
        },
        onApplied,
        onClose: onCancel,
    });

    const handleDownload = async () => {
        try {
            const [result, hubsResult] = await Promise.all([loadTiers(), loadHubs()]);
            if (result.error) throw result.error;
            if (hubsResult.error) throw hubsResult.error;
            const tiers = result.data?.onboardingOrderQuotaCustomerTiers ?? [];
            const hubs = (hubsResult.data?.onboardingOrderQuotaHubOverview ?? []).map((hub) => ({
                hubCode: hub.hubCode,
                hubName: hub.hubName,
                optional: hub.isTest,
            }));
            downloadWorkbook(
                buildTierWorkbook(
                    tiers,
                    {
                        channelId: t(TIER_HEADER_KEYS[0]),
                        name: t(TIER_HEADER_KEYS[1]),
                        tier: t(TIER_HEADER_KEYS[2]),
                        optionalHubMark: t(OPTIONAL_HUB_MARK_KEY),
                    },
                    (tier) => t(`central.orderQuota.tier.${tier}`),
                    hubs
                ),
                tierFileName(today)
            );
        } catch (e) {
            message.error((e as Error).message);
        }
    };

    return (
        <Modal
            open={open}
            width={720}
            title={t('central.orderQuota.customerTier.title')}
            okText={t('central.orderQuota.import.confirm')}
            cancelText={t('common.cancel')}
            okButtonProps={{ disabled: blocked, loading }}
            onCancel={onCancel}
            onOk={handleConfirm}
            destroyOnClose
        >
            <div className="flex flex-col gap-4">
                <LineList className="text-[#8c8c8c]" lines={(t('central.orderQuota.customerTier.hint') as string).split('\n')} />
                <div className="flex items-center gap-3">
                    <Button icon={<DownloadOutlined />} onClick={handleDownload}>
                        {t('central.orderQuota.customerTier.download')}
                    </Button>
                    <Upload accept=".xlsx,.xls,.csv" showUploadList={false} beforeUpload={handleFile}>
                        <Button icon={<UploadOutlined />} loading={loading}>
                            {t('central.orderQuota.import.upload')}
                        </Button>
                    </Upload>
                </div>
                {rejection && <ImportRejection reason={rejection} />}
                {preview && (
                    <ImportPreview
                        preview={preview}
                        codeHeader={t(TIER_HEADER_KEYS[0])}
                        summary={
                            t('central.orderQuota.customerTier.summary', {
                                total: preview.total,
                                valid: preview.validCount,
                                created: preview.newCount,
                                updated: preview.updateCount,
                                unchanged: preview.unchangedCount,
                                errors: preview.errors.length,
                            }) as string
                        }
                    />
                )}
            </div>
        </Modal>
    );
};

export default CustomerTierModal;
